import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Avatar } from 'ui/avatar';
import { Tooltip } from 'ui/tooltip';
import { DiamondLogo, LogoColors } from 'ui/logo';
import { Locked16 } from 'ui/icons';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { useViewContext } from 'modules/messagingInsights/context';
import {
  selectSavedViews,
  selectRecentSavedViews,
  selectSavedViewsPaginationOpts,
  selectProjectDefaultView,
} from '../../../selectors';
import { fetchSavedViewsInfiniteScrollRequest } from '../../../actions';
import {
  convertQueryParamsToString,
  sortObjectArrayByKeyArray,
  sortViewsByUpdatedAt,
} from '../../../utils';
import { SavedView } from '../../../types';
import { FlexAlignCenter } from '../../../Styled';

const TruncationWrapper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const parseOptionsFromRecords = (
  records: any[] = [],
  categoryId?: string,
  excludedIds?: number[],
) => {
  return records
    .filter((record) => !excludedIds || !excludedIds.includes(record.id))
    .map((record: any) => {
      const {
        id,
        name,
        isPrivate,
        isProjectDefault,
        createdByFirstName,
        createdByLastName,
        createdByAvatarUrl,
      } = record;
      return {
        id,
        label: <TruncationWrapper>{name}</TruncationWrapper>,
        value: `${id}`,
        categoryId,
        badgeText: isProjectDefault && 'default',
        Suffix: (
          <FlexAlignCenter>
            {isPrivate && (
              <Tooltip tooltipContent="Only you have access">
                <Locked16 />
              </Tooltip>
            )}
            {isProjectDefault ? (
              <DiamondLogo color={LogoColors.BLACK} height={24} />
            ) : (
              <Tooltip tooltipContent={`Created by ${createdByFirstName} ${createdByLastName}`}>
                <Avatar
                  firstName={createdByFirstName}
                  lastName={createdByLastName}
                  imgSrc={createdByAvatarUrl}
                />
              </Tooltip>
            )}
          </FlexAlignCenter>
        ),
      };
    });
};

export const useSavedViewsDropdownOptions = () => {
  const dispatch = useDispatch();
  const [debouncedSearchString, searchString, setSearchString] = useDebouncedState('', 1000);
  const [isSearching, setIsSearching] = useState(false);
  const savedViews = useSelector(selectSavedViews);
  const savedViewsPaginationOpts = useSelector(selectSavedViewsPaginationOpts);
  const projectDefaultView = useSelector(selectProjectDefaultView);

  // Endpoint doesn't preserve view ID order, so sort again based on recentlyViewedIds
  const { recentlyViewedIds } = useViewContext();
  const recentSavedViews = useSelector(selectRecentSavedViews) || [];
  const sortedRecentSavedViews = sortObjectArrayByKeyArray(
    'id',
    recentlyViewedIds.map((i) => parseInt(i, 10)),
    recentSavedViews,
  ) as SavedView[];

  const { page, pageCount, pageSize = 25 } = savedViewsPaginationOpts || {};
  const canFetch = !!page && !!pageCount && page < pageCount;
  const onFetch = () => {
    const queryParams = {
      page: parseInt(page, 10) + 1,
      pageSize,
      searchString,
    };
    const queryString = convertQueryParamsToString(queryParams);
    dispatch(fetchSavedViewsInfiniteScrollRequest(queryString));
  };

  useEffect(() => {
    if (debouncedSearchString === searchString) {
      const queryParams = {
        page: 1,
        pageSize,
        searchString,
      };
      const queryString = convertQueryParamsToString(queryParams);
      dispatch(fetchSavedViewsInfiniteScrollRequest(queryString));
    }
  }, [debouncedSearchString]);

  useEffect(() => {
    setIsSearching(false);
  }, [savedViews]);

  if (savedViews?.length > 12) {
    const excludedIdsFromRecentlyVisited = projectDefaultView ? [projectDefaultView.id] : [];
    const sortedRecentSavedViewsIds = sortedRecentSavedViews.length
      ? sortedRecentSavedViews.map((view: SavedView) => view.id)
      : [];
    const excludedIdsFromAllViews = [
      ...new Set([...excludedIdsFromRecentlyVisited, ...sortedRecentSavedViewsIds]),
    ];

    const recentlyVisitedViewsCount =
      projectDefaultView && sortedRecentSavedViewsIds.includes(projectDefaultView.id)
        ? sortedRecentSavedViewsIds.length - 1
        : sortedRecentSavedViewsIds.length;
    const SavedViewsCategoryLabels = {
      projectDefault: 'Project Default',
      recentlyVisited: `Recently Visited (${recentlyVisitedViewsCount})`,
      allViews: `All views (${
        savedViewsPaginationOpts.totalCount - excludedIdsFromAllViews.length
      })`,
    };
    const projectDefaultOption = projectDefaultView
      ? parseOptionsFromRecords([projectDefaultView], SavedViewsCategoryLabels.projectDefault)
      : [];
    const recentlyVisitedOptions = parseOptionsFromRecords(
      sortedRecentSavedViews,
      SavedViewsCategoryLabels.recentlyVisited,
      excludedIdsFromRecentlyVisited,
    );
    const allViewsOptions = parseOptionsFromRecords(
      savedViews,
      SavedViewsCategoryLabels.allViews,
      excludedIdsFromAllViews,
    );
    return {
      canFetch,
      options: [...projectDefaultOption, ...recentlyVisitedOptions, ...allViewsOptions],
      onFetch,
      onSearchChange: setSearchString,
      searchString,
      setIsSearching,
      isSearching,
    };
  } else {
    savedViews.sort(sortViewsByUpdatedAt);
    return {
      canFetch,
      options: parseOptionsFromRecords(savedViews),
      onFetch,
      onSearchChange: setSearchString,
      searchString,
      setIsSearching,
      isSearching,
    };
  }
};
